import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { TcorFollowingSteps } from '@frontend/components/pages/customControls/TCOR/v1/TcorFollowingSteps';
import type { IPatientContactInfoDraft } from '../../../../../../common/validation/patientContactInfo';
import type { IMedication } from '../../../../interfaces/medication';
import type { IRequestAddEdit } from '../../../requests/RequestItemAddEdit';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import type { IKlachten } from '../../TCCr/v1/KlachtenAddEditModal';
import type { ILichamelijkOnderzoek } from '../../TCCr/v1/LichamelijkOnderzoek';
import type { IRedenen } from '../../TCCr/v1/RedenenAddEditModal';
import type { IRisicofactoren } from '../../TCCr/v1/Risicofactoren';
import type { IVoorgeschiedenis } from '../../TCCr/v1/Voorgeschiedenis';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { BmiText } from '../../../../elements/BmiText';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormAnamnesisButtons,
    FormDivider,
    HeaderDivider,
    InputNumber,
    InputTextArea,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { PatientContactAddEdit } from '../../../../elements/PatientContactAddEdit';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { KlachtenAddEdit } from '../../TCCr/v1/Klachten';
import { LichamelijkOnderzoekAddEditModal } from '../../TCCr/v1/LichamelijkOnderzoek';
import { RedenenAddEdit } from '../../TCCr/v1/Redenen';
import { RisicofactorenAddEditModal } from '../../TCCr/v1/Risicofactoren';
import { VoorgeschiedenisAddEditModal } from '../../TCCr/v1/Voorgeschiedenis';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { ContinueWithoutMedicationsModal } from '../../utils/v1/medications/ContinueWithoutMedicationsModal';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';
import { AnamneseType } from './anamneseType';
import { BloeddrukAddEdit } from './BloeddrukAddEdit';

interface IInnerTcorV1RequestAddEditState {
    buttonDisabled?: boolean;
    proceedWithoutMedications?: boolean;
    showAnamneseType: AnamneseType | null;
}
export interface ICustomControlData {
    bloeddruk_diastolisch: string | null;
    bloeddruk_systolisch: string | null;
    contactgegevens_patient: IPatientContactInfoDraft;
    gewicht: number | null;
    klacht: string | null;
    klachten: IKlachten | null;
    lengte: number | null;
    lichamelijkOnderzoek: ILichamelijkOnderzoek | null;
    patientUsesMedication: YesOrNo | null;
    reden: string | null;
    redenen: IRedenen | null;
    risicofactoren: IRisicofactoren | null;
    toelichting: string | null;
    voorgeschiedenis: IVoorgeschiedenis | null;
}

interface IRequestAddEditProps extends IRequestAddEdit {
    customControlData: ICustomControlData;
}

interface IInnerTcorV1RequestAddEditProps extends ICustomControlRequestAddEditProps {
    request: IRequestAddEditProps;
}

export function TcorV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    return <InnerTcorV1RequestAddEdit {...props} />;
}

class InnerTcorV1RequestAddEdit extends Component<IInnerTcorV1RequestAddEditProps, IInnerTcorV1RequestAddEditState> {
    public async componentDidMount() {
        await this.props.handleChangeCustomControl({
            ...tcorV1InitializeCustomControlData(),
            ...this.props.request.customControlData,
        });
    }

    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isExaminationRequest
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                {this.addEditForm()}
            </NewOrEditRequestProcessContainer>
        );
    }

    private addEditForm() {
        return (
            <Form onSubmit={async () => null}>
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.request}
                />

                <ServiceOrganizationProvider
                    errors={this.props.errors}
                    initialOrganizationId={this.props.request.selectedProcedureProviderOrganizationId}
                    initialPractitionerId={this.props.request.selectedProcedureProviderPractitionerId}
                    isExaminationRequest
                    onChange={async (organizationId, practitionerId) => {
                        await this.props.handleChange('selectedProcedureProviderOrganizationId', organizationId);
                        await this.props.handleChange('selectedProcedureProviderPractitionerId', practitionerId);
                    }}
                    onDoValidate={async () => await this.props.validateOne('selectedProcedureProviderOrganizationId')}
                    roleOfPractitionersInList={PractitionerRoleInRequest.biometrician}
                    serviceProviderServiceId={this.props.service.id}
                />

                <FormDivider />

                <FormContextProvider
                    value={{
                        placeholderGroupName: 'request',
                        item: this.props.request.customControlData,
                        errors: this.props.errors,
                        onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await this.props.validateOneCustomControl(key),
                    }}
                >
                    <HeaderDivider text="Anamnese" />
                    <KlachtenAddEdit
                        customControlData={this.props.request.customControlData}
                        errors={this.props.errors}
                        handleChange={this.props.handleChangeCustomControl}
                        isRequired
                        validateOne={this.props.validateOneCustomControl}
                    />
                    <RedenenAddEdit
                        customControlData={this.props.request.customControlData}
                        errors={this.props.errors}
                        handleChange={this.props.handleChangeCustomControl}
                        isRequired
                        validateOne={this.props.validateOneCustomControl}
                    />

                    <BloeddrukAddEdit
                        customControlData={this.props.request.customControlData}
                        errors={this.props.errors}
                        handleChange={this.props.handleChangeCustomControl}
                        validateOne={this.props.validateOneCustomControl}
                    />

                    <HorizontalFormGroup
                        hasErrors={
                            this.props.errors.has('customControlData.gewicht') ||
                            this.props.errors.has('customControlData.lengte')
                        }
                    >
                        <FormLabel>BMI</FormLabel>
                        <InputContainer size="auto">
                            <InputNumber
                                automation="automation-BMI-weight"
                                error={this.props.errors.get('customControlData.gewicht')}
                                fieldKey="gewicht"
                                placeholder={translateKey('common.weight')}
                                text="kg"
                                textStyle={{ fontWeight: 'bold' }}
                                width="medium"
                            />
                        </InputContainer>
                        <InputContainer size="auto">
                            <InputNumber
                                automation="automation-BMI-height"
                                error={this.props.errors.get('customControlData.lengte')}
                                fieldKey="lengte"
                                placeholder={translateKey('common.length')}
                                text="cm"
                                textStyle={{ fontWeight: 'bold' }}
                                width="medium"
                            />
                        </InputContainer>
                        <InputContainer size="small">
                            <BmiText
                                length={this.props.request.customControlData.lengte}
                                weight={this.props.request.customControlData.gewicht}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    <HorizontalFormGroup>
                        <FormLabel>Meer informatie toevoegen</FormLabel>
                        <InputContainer>
                            <FormAnamnesisButtons
                                buttons={[
                                    <CheckMarkButton
                                        isChecked={!!this.props.request.customControlData.voorgeschiedenis}
                                        onClick={async () =>
                                            await this.setState({ showAnamneseType: AnamneseType.voorgeschiedenis })
                                        }
                                    >
                                        Voorgeschiedenis
                                    </CheckMarkButton>,
                                    <CheckMarkButton
                                        isChecked={!!this.props.request.customControlData.risicofactoren}
                                        onClick={async () =>
                                            await this.setState({ showAnamneseType: AnamneseType.risicofactoren })
                                        }
                                    >
                                        Risicofactoren
                                    </CheckMarkButton>,
                                    <CheckMarkButton
                                        isChecked={!!this.props.request.customControlData.lichamelijkOnderzoek}
                                        onClick={async () =>
                                            await this.setState({ showAnamneseType: AnamneseType.lichamelijkOnderzoek })
                                        }
                                    >
                                        Lichamelijk onderzoek
                                    </CheckMarkButton>,
                                ]}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>

                    {this.state.showAnamneseType === AnamneseType.voorgeschiedenis && (
                        <VoorgeschiedenisAddEditModal
                            anamneseData={this.props.request.customControlData.voorgeschiedenis}
                            deleteAnamneseData={async () =>
                                await this.deleteAnamneseData(AnamneseType.voorgeschiedenis)
                            }
                            onClose={async () => await this.setState({ showAnamneseType: null })}
                            setAnamneseData={async (voorgeschiedenis) =>
                                await this.setAnamneseData(AnamneseType.voorgeschiedenis, voorgeschiedenis)
                            }
                        />
                    )}

                    {this.state.showAnamneseType === AnamneseType.risicofactoren && (
                        <RisicofactorenAddEditModal
                            anamneseData={this.props.request.customControlData.risicofactoren}
                            deleteAnamneseData={async () => await this.deleteAnamneseData(AnamneseType.risicofactoren)}
                            onClose={async () => await this.setState({ showAnamneseType: null })}
                            setAnamneseData={async (risicofactor) =>
                                await this.setAnamneseData(AnamneseType.risicofactoren, risicofactor)
                            }
                        />
                    )}

                    {this.state.showAnamneseType === AnamneseType.lichamelijkOnderzoek && (
                        <LichamelijkOnderzoekAddEditModal
                            anamneseData={this.props.request.customControlData.lichamelijkOnderzoek}
                            deleteAnamneseData={async () =>
                                await this.deleteAnamneseData(AnamneseType.lichamelijkOnderzoek)
                            }
                            onClose={async () => await this.setState({ showAnamneseType: null })}
                            setAnamneseData={async (onderzoek) =>
                                await this.setAnamneseData(AnamneseType.lichamelijkOnderzoek, onderzoek)
                            }
                        />
                    )}

                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.toelichting')}>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                automation="remark"
                                error={this.props.errors.get('customControlData.toelichting')}
                                fieldKey="toelichting"
                                placeholder="Een toelichting, opmerkingen of vragen..."
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                </FormContextProvider>

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await this.props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await this.props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={this.props.request.patientUsesMedication}
                    validateMedications={async () => await this.props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await this.props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangeAllergies={async (allergies) => await this.props.handleChange('allergies', allergies)}
                    handleChangeCustomControl={this.props.handleChangeCustomControl}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await this.props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await this.props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await this.props.validateOne('requestEpisodesAndJournal')}
                    validateOneCustomControl={this.props.validateOneCustomControl}
                />

                <PatientContactAddEdit {...this.props} />
                <TcorFollowingSteps />
                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.request}
                            isExisting={!!this.props.request.id}
                            revertChanges={this.props.revertRequestChanges}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveRequest('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveRequest('final');
                            }}
                        />,
                    ]}
                />

                <ContinueWithoutMedicationsModal
                    handleNo={async () =>
                        await this.setState({ proceedWithoutMedications: false, buttonDisabled: false })
                    }
                    handleYes={async () => await this.saveRequest('final')}
                    isOpen={!!this.state.proceedWithoutMedications}
                />
            </Form>
        );
    }

    private async setAnamneseData(
        typeOfAnamnese: AnamneseType,
        item: IVoorgeschiedenis | IRisicofactoren | ILichamelijkOnderzoek | undefined
    ) {
        await this.props.handleChangeCustomControl({ [typeOfAnamnese]: { ...item } });
        await this.setState({ showAnamneseType: null });
    }

    private async deleteAnamneseData(typeOfAnamnese: AnamneseType) {
        await this.props.handleChangeCustomControl({ [typeOfAnamnese]: null });
        await this.setState({ showAnamneseType: null });
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (
            !this.state.proceedWithoutMedications &&
            (!this.props.request.requestMedications || this.props.request.requestMedications.length === 0) &&
            (this.props.request.patientUsesMedication ? YesOrNo.yes : YesOrNo.no) === YesOrNo.yes &&
            saveType === 'final'
        ) {
            await this.setState({ proceedWithoutMedications: true });

            return;
        }

        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                this.props.service,
                'request',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
        }
        await this.props.onSave(requestId);
    }
}

export function tcorV1InitializeCustomControlData() {
    return {
        voorgeschiedenis: null,
        risicofactoren: null,
        lichamelijkOnderzoek: null,
        klachten: null,
        klacht: null,
        redenen: null,
        reden: null,
        bloeddruk_systolisch: null,
        bloeddruk_diastolisch: null,
        gewicht: null,
        lengte: null,
        toelichting: null,
        contactgegevens_patient: {
            emailadres: null,
            mobileNumber: null,
            homePhoneNumber: null,
        },
    };
}
